import { useRef, useState, useCallback, useEffect } from "react";
import { MoveHorizontal } from "lucide-react";

// Lightweight before/after drag comparison (same style used site-wide).
export const Compare = ({ before, after, beforeAlt, afterAlt, testId, className = "" }) => {
  const containerRef = useRef(null);
  const [pos, setPos] = useState(50);
  const [width, setWidth] = useState(0);
  const dragging = useRef(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setWidth(el.offsetWidth);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const setFromClientX = useCallback((clientX) => {
    const el = containerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    setPos(Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100)));
  }, []);

  useEffect(() => {
    const move = (e) => {
      if (!dragging.current) return;
      setFromClientX(e.touches ? e.touches[0].clientX : e.clientX);
    };
    const up = () => (dragging.current = false);
    window.addEventListener("mousemove", move);
    window.addEventListener("touchmove", move, { passive: false });
    window.addEventListener("mouseup", up);
    window.addEventListener("touchend", up);
    return () => {
      window.removeEventListener("mousemove", move);
      window.removeEventListener("touchmove", move);
      window.removeEventListener("mouseup", up);
      window.removeEventListener("touchend", up);
    };
  }, [setFromClientX]);

  return (
    <div
      ref={containerRef}
      data-testid={testId}
      className={`relative w-full overflow-hidden rounded-3xl ring-1 ring-navy/10 shadow-lg no-select cursor-ew-resize ${className}`}
      onMouseDown={(e) => {
        dragging.current = true;
        setFromClientX(e.clientX);
      }}
      onTouchStart={(e) => {
        dragging.current = true;
        setFromClientX(e.touches[0].clientX);
      }}
    >
      {/* AFTER (base) */}
      <img
        src={after}
        alt={afterAlt}
        draggable={false}
        className="absolute inset-0 h-full w-full object-cover"
      />
      <span className="absolute right-4 top-4 z-20 rounded-full bg-turquoise px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white shadow-lg">
        After
      </span>

      {/* BEFORE (clipped overlay) */}
      <div className="absolute inset-0 overflow-hidden" style={{ width: `${pos}%` }}>
        <img
          src={before}
          alt={beforeAlt}
          draggable={false}
          style={{ width: width || "100%" }}
          className="absolute inset-0 h-full max-w-none object-cover"
        />
        <span className="absolute left-4 top-4 z-20 rounded-full bg-navy px-4 py-1.5 text-xs font-bold uppercase tracking-wider text-white shadow-lg">
          Before
        </span>
      </div>

      {/* Handle */}
      <div
        className="absolute inset-y-0 z-30 flex items-center"
        style={{ left: `${pos}%`, transform: "translateX(-50%)" }}
      >
        <div className="h-full w-0.5 bg-white shadow-[0_0_12px_rgba(0,0,0,0.35)]" />
        <div className="absolute left-1/2 top-1/2 flex h-11 w-11 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full bg-white text-navy shadow-xl ring-2 ring-turquoise">
          <MoveHorizontal className="h-5 w-5" />
        </div>
      </div>
    </div>
  );
};
